import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  fetchAuditsApiThunk,
  createAuditApiThunk,
  updateAuditApiThunk,
  reopenAuditApiThunk,
  searchAuditsApiThunk,
  getAuditSummaryApiThunk,
  getAuditStatsApiThunk,
  resetAllApiStatus,
  setCurrentAudit,
  setAuditSummary,
  clearAPIStatusMessages,
  getAuditByIdApiThunk,
} from './auditsSlice';
import { RootState } from '..';
import {
  Audit,
  CreateAuditRequest,
  GetAuditStatsRequest,
  GetAuditSummaryRequest,
  GetAuditSummaryResponse,
  ListAuditsRequest,
  SearchAuditsRequest,
  UpdateAuditRequest,
  ReopenAuditRequest,
  GetAuditRequest,
} from 'src/types/helios/Audit';

export const useAudits = () => {
  const dispatch = useAppDispatch();

  const auditsList = useAppSelector((state: RootState) => state.audits.auditsList);
  const fetchAuditsApiStatus = useAppSelector(
    (state: RootState) => state.audits.fetchAuditsApiStatus,
  );
  const createAuditApiStatus = useAppSelector(
    (state: RootState) => state.audits.createAuditApiStatus,
  );
  const updateAuditApiStatus = useAppSelector(
    (state: RootState) => state.audits.updateAuditApiStatus,
  );
  const reopenAuditApiStatus = useAppSelector(
    (state: RootState) => state.audits.reopenAuditApiStatus,
  );
  const searchAuditsApiStatus = useAppSelector(
    (state: RootState) => state.audits.searchAuditsApiStatus,
  );
  const auditSummaryApiStatus = useAppSelector(
    (state: RootState) => state.audits.auditSummaryApiStatus,
  );
  const auditStatsApiStatus = useAppSelector(
    (state: RootState) => state.audits.auditStatsApiStatus,
  );

  const currentAudit = useAppSelector((state: RootState) => state.audits.currentAudit);
  const auditSummary = useAppSelector((state: RootState) => state.audits.auditSummary);
  const auditStats = useAppSelector((state: RootState) => state.audits.auditStats);

  const loadAuditsList = useCallback(
    async (data: ListAuditsRequest) => {
      dispatch(fetchAuditsApiThunk(data));
    },
    [dispatch],
  );

  const createAudit = useCallback(
    async (data: CreateAuditRequest) => {
      dispatch(createAuditApiThunk(data));
    },
    [dispatch],
  );

  const updateAudit = useCallback(
    async (data: UpdateAuditRequest) => {
      dispatch(updateAuditApiThunk(data));
    },
    [dispatch],
  );

  const reopenAudit = useCallback(
    async (data: ReopenAuditRequest) => {
      dispatch(reopenAuditApiThunk(data));
    },
    [dispatch],
  );

  const resetApiStatus = useCallback(() => {
    dispatch(resetAllApiStatus());
  }, [dispatch]);

  const clearStatusMessages = useCallback(() => {
    dispatch(clearAPIStatusMessages());
  }, [dispatch]);

  const searchAudits = useCallback(
    async (data: SearchAuditsRequest) => {
      if (
        data.dateRange?.type === 'CUSTOM' &&
        (!data.dateRange.startDate || !data.dateRange.endDate)
      ) {
        return;
      }

      const newData = {
        ...data,
        dateRange: data.dateRange
          ? {
              ...data.dateRange,
              startDate: data.dateRange.startDate
                ? data.dateRange.startDate + 'T00:00:00Z'
                : data.dateRange.startDate,
              endDate: data.dateRange.endDate
                ? data.dateRange.endDate + 'T00:00:00Z'
                : data.dateRange.endDate,
            }
          : data.dateRange,
      };

      dispatch(searchAuditsApiThunk(newData));
    },
    [dispatch],
  );

  const selectCurrentAudit = useCallback(
    async (data?: Audit) => {
      dispatch(setCurrentAudit(data));
    },
    [dispatch],
  );

  const selectAuditSummary = useCallback(
    async (data?: GetAuditSummaryResponse) => {
      dispatch(setAuditSummary(data));
    },
    [dispatch],
  );

  const getAuditSummary = useCallback(
    async (data: GetAuditSummaryRequest) => {
      dispatch(getAuditSummaryApiThunk(data));
    },
    [dispatch],
  );

  const getAuditById = useCallback(
    async (data: GetAuditRequest) => {
      dispatch(getAuditByIdApiThunk(data));
    },
    [dispatch],
  );

  const getAuditStats = useCallback(
    async (data: GetAuditStatsRequest) => {
      dispatch(getAuditStatsApiThunk(data));
    },
    [dispatch],
  );

  return {
    auditsList,
    fetchAuditsApiStatus,
    createAuditApiStatus,
    updateAuditApiStatus,
    reopenAuditApiStatus,
    searchAuditsApiStatus,
    auditSummaryApiStatus,
    auditStatsApiStatus,
    currentAudit,
    auditSummary,
    auditStats,
    selectCurrentAudit,
    selectAuditSummary,
    loadAuditsList,
    createAudit,
    updateAudit,
    reopenAudit,
    searchAudits,
    getAuditSummary,
    getAuditStats,
    resetApiStatus,
    clearStatusMessages,
    getAuditById,
  };
};
